// v10/src/services/apiService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Storage helpers ───────────────────────────────────────────────
const USER_KEY    = 'condor360_current_user';
const REFRESH_KEY = 'condor360_refresh_token';

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const getRefreshToken = () =>
  localStorage.getItem(REFRESH_KEY) || sessionStorage.getItem(REFRESH_KEY) || null;

export const storeTokens = (accessToken, refreshToken, user) => {
  // ✅ fix: siempre localStorage para que el token sobreviva al cerrar la app
  localStorage.setItem(USER_KEY, JSON.stringify({ ...user, token: accessToken }));
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  sessionStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
};

export const clearSession = () => {
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
};

// ── Actividad del usuario ─────────────────────────────────────────
// Registramos el último evento del usuario para decidir si renovar
// silenciosamente o mostrar el modal de advertencia.
let lastActivityAt = Date.now();
const updateActivity = () => { lastActivityAt = Date.now(); };
['mousemove', 'keydown', 'click', 'touchstart', 'scroll', 'pointerdown'].forEach(evt =>
  window.addEventListener(evt, updateActivity, { passive: true })
);

// true si el usuario tuvo actividad en los últimos thresholdMs ms (default 10 min)
const isUserActive = (thresholdMs = 10 * 60 * 1000) =>
  (Date.now() - lastActivityAt) < thresholdMs;

// ── Axios instance ────────────────────────────────────────────────
const api = axios.create({ baseURL: API_URL });

// ── Request interceptor — adjunta access token ────────────────────
api.interceptors.request.use(config => {
  try {
    const user = getStoredUser();
    if (user?.token) config.headers.Authorization = `Bearer ${user.token}`;
  } catch {}
  return config;
});

// ── Refresh logic — evitar múltiples refreshes simultáneos ────────
let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach(cb => error ? cb.reject(error) : cb.resolve(token));
  refreshQueue = [];
};

// Función interna reutilizable para hacer el refresh
const doRefresh = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token');

  const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
  const { token: newAccessToken, refreshToken: newRefreshToken, user, expiresIn } = response.data;
  storeTokens(newAccessToken, newRefreshToken, user);
  // Reprogramar el timer con el nuevo token
  scheduleExpiryWarning(expiresIn || 900);
  return newAccessToken;
};

// ── Response interceptor — auto-refresh en 401 ───────────────────
api.interceptors.response.use(
  res => res,
  async err => {
    const originalRequest = err.config;

    // ── Demo gate — el backend rechaza acciones sensibles en org demo ──
    // Cuando el usuario demo intenta gastar plata / destruir vidriera /
    // enviar comunicación al exterior, el back devuelve 403 con `demo:true`.
    // Emitimos un evento para que el modal comercial global (App.js) lo
    // muestre con CTA en vez de un error genérico. No re-rechazamos: el
    // Promise.reject sigue para que el caller pueda seguir su lógica.
    if (err.response?.status === 403 && err.response?.data?.demo === true) {
      window.dispatchEvent(new CustomEvent('condor360:demo-blocked', {
        detail: { message: err.response.data.message || '' },
      }));
    }

    if (err.response?.status === 401 && originalRequest.url?.includes('/auth/refresh')) {
      clearSession();
      window.dispatchEvent(new CustomEvent('condor360:session-expired'));
      return Promise.reject(err);
    }

    if (err.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clearSession();
        window.dispatchEvent(new CustomEvent('condor360:session-expired'));
        return Promise.reject(err);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newAccessToken = await doRefresh();
        processQueue(null, newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        clearSession();
        window.dispatchEvent(new CustomEvent('condor360:session-expired'));
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  }
);

// ── Session expiry warning ────────────────────────────────────────
//
// Se llama después de cada login/refresh con el tiempo de vida del access token.
//
// Cuando faltan 2 minutos para que expire:
//   ✅ Usuario activo (click/touch en los últimos 10 min)
//      → Renovar silenciosamente, sin interrumpir al piloto durante el chequeo.
//   ⏰ Usuario inactivo (dejó la pestaña abierta y no toca nada)
//      → Mostrar el modal de aviso "tu sesión está por vencer".
//
// Resultado: un piloto chequeando ítem por ítem NUNCA ve el modal.
// Un admin que volvió después de mucho tiempo sí lo ve.

let expiryWarningTimer = null;

export const scheduleExpiryWarning = (expiresInSeconds = 900) => {
  if (expiryWarningTimer) clearTimeout(expiryWarningTimer);

  const warnAt = (expiresInSeconds - 120) * 1000; // 2 min antes del vencimiento
  if (warnAt <= 0) return;

  expiryWarningTimer = setTimeout(async () => {
    expiryWarningTimer = null;

    if (isUserActive()) {
      // Renovar silenciosamente — scheduleExpiryWarning se reprograma dentro de doRefresh
      try {
        await doRefresh();
      } catch {
        // Refresh falló (cuenta desactivada, token revocado, etc.) → mostrar modal
        window.dispatchEvent(new CustomEvent('condor360:session-expiring-soon'));
      }
    } else {
      // Usuario inactivo → mostrar aviso
      window.dispatchEvent(new CustomEvent('condor360:session-expiring-soon'));
    }
  }, warnAt);
};

export const cancelExpiryWarning = () => {
  if (expiryWarningTimer) {
    clearTimeout(expiryWarningTimer);
    expiryWarningTimer = null;
  }
};

// ── Auth ──────────────────────────────────────────────────────────
export const authService = {
  login:     (data) => api.post('/auth/login', data),
  register:  (data) => api.post('/auth/register', data),
  me:        ()     => api.get('/auth/me'),
  demoLogin: (role = 'admin') => api.post('/auth/demo-login', { role }),

  logout: () => {
    const refreshToken = getRefreshToken();
    cancelExpiryWarning();
    clearSession();
    if (refreshToken) {
      axios.post(`${API_URL}/auth/logout`, { refreshToken }).catch(() => {});
    }
  },

  logoutAll: () => api.post('/auth/logout-all'),
};

// ── Users ─────────────────────────────────────────────────────────
export const userService = {
  getAll:  (params = {}) => api.get('/api/users', { params }),
  getById: (id)          => api.get(`/api/users/${id}`),
  update:  (id, data)    => api.put(`/api/users/${id}`, data),
  delete:  (id)          => api.delete(`/api/users/${id}`),
};

// ── Aircraft ──────────────────────────────────────────────────────
export const aircraftService = {
  getAll:  (params = {}) => api.get('/api/aircraft', { params }),
  getById: (id, lang, phase) => api.get(`/api/aircraft/${id}`, { params: { ...(lang ? { lang } : {}), ...(phase ? { phase } : {}) } }),

  create:  (data)        => api.post('/api/aircraft', data),
  update:  (id, data)    => api.put(`/api/aircraft/${id}`, data),
  delete:  (id)          => api.delete(`/api/aircraft/${id}`),
};

// ── Hobbs (vuelos) ────────────────────────────────────────────────
// Estados: 'open' | 'closed' | 'annulled'.
export const hobbsService = {
  getPending: ()                  => api.get('/api/aircraft/hobbs/pending'),
  getHistory: (limit = 30)        => api.get('/api/aircraft/hobbs/history', { params: { limit } }),
  complete:   (recordId, data)    => api.patch(`/api/aircraft/hobbs/${recordId}/complete`, data),
  annul:      (recordId, reason)  => api.patch(`/api/aircraft/hobbs/${recordId}/annul`, { reason }),
};

// ── Flights (registro rápido) ─────────────────────────────────────
// Parte 4 — Abre un vuelo sin pasar por el checklist. El cierre usa
// hobbsService.complete (mismo flujo que un vuelo del checklist).
export const flightService = {
  quickOpen: (data) => api.post('/api/flights/quick-open', data),
};

// ── Assignments ───────────────────────────────────────────────────
export const assignmentService = {
  getAll:   ()                     => api.get('/api/aircraft/assignments/all'),
  assign:   (user_id, aircraft_id) => api.post('/api/aircraft/assignments', { user_id, aircraft_id }),
  unassign: (user_id, aircraft_id) => api.delete(`/api/aircraft/assignments/${user_id}/${aircraft_id}`),
};

// ── Checklists ────────────────────────────────────────────────────
export const checklistService = {
  getByAircraft: (aircraftId)    => api.get(`/api/checklists/${aircraftId}`),
  getById:       (id)            => api.get(`/api/checklists/${id}`),
  create:        (data)          => api.post('/api/checklists', data),
  update:        (id, data)      => api.put(`/api/checklists/${id}`, data),
  delete:        (id)            => api.delete(`/api/checklists/${id}`),
  createItem:    (data)          => api.post('/api/checklists/items', data),
  updateItem:    (id, data)      => api.put(`/api/checklists/items/${id}`, data),
  deleteItem:    (id, permanent) => api.delete(`/api/checklists/items/${id}${permanent ? '?permanent=true' : ''}`),
};

// ── Inspections ───────────────────────────────────────────────────
export const inspectionService = {
  getAll:   (params = {}) => api.get('/api/inspections', { params }),
  getById:  (id)          => api.get(`/api/inspections/${id}`),
  getGroup: (groupId)     => api.get(`/api/inspections/groups/${groupId}`), // ✅ Sub-etapa 3.3 — detalle de contenedor multi-fase
  start:    (data)        => api.post('/api/inspections/start', data),  // ✅ NUEVO — crea registro in_progress
  create:   (data)        => api.post('/api/inspections', data),
  update:   (id, data)    => api.put(`/api/inspections/${id}`, data),
  getStats: ()            => api.get('/api/inspections/stats'),
};

// ── Notifications ─────────────────────────────────────────────────
export const notificationService = {
  getAll:            ()          => api.get('/api/notifications'),
  updateLicense:     (data)      => api.put('/api/notifications/license', data),
  updateLicenseFor:  (uid, data) => api.put(`/api/notifications/license/${uid}`, data),
  updateMaintenance: (id, data)  => api.put(`/api/notifications/aircraft/${id}/maintenance`, data),
};

// -- Prelanding check
export const preLandingService = {
  getSteps:     (aircraftId)         => api.get(`/api/aircraft/${aircraftId}/pre-landing`),
  saveSteps:    (aircraftId, steps)  => api.put(`/api/aircraft/${aircraftId}/pre-landing`, { steps }),
  seedDefaults: (aircraftId)         => api.post(`/api/aircraft/${aircraftId}/pre-landing/seed`),
};

// ── Flow Mode ────────────────────────────────────────────────────
export const flowService = {
  getChecklist:   (aircraftId)         => api.get(`/api/flow/checklist/${aircraftId}`),
  registerEvent:  (data)               => api.post('/api/flow/event', data),
  complete:       (data)               => api.post('/api/flow/complete', data),
  getStats:       (pilotId)            => api.get(`/api/flow/stats/${pilotId}`),
  getTrapItems:   (aircraftId)         => api.get(`/api/flow/trap-items/${aircraftId}`),
  createTrapItem: (data)               => api.post('/api/flow/trap-items', data),
  deleteTrapItem: (id)                 => api.delete(`/api/flow/trap-items/${id}`),
  toggleFlowMode: (pilotId, enabled)   => api.put(`/api/flow/unlock/${pilotId}`, { enabled }),
};

// ── Emergency Trainer ────────────────────────────────────────────
export const emergencyService = {
  listProcedures:  (aircraftId)            => api.get(`/api/emergency/procedures/${aircraftId}`),
  createProcedure: (data)                  => api.post('/api/emergency/procedures', data),
  updateProcedure: (id, data)              => api.put(`/api/emergency/procedures/${id}`, data),
  deleteProcedure: (id)                    => api.delete(`/api/emergency/procedures/${id}`),
  scanProcedure:   (data)                  => api.post('/api/emergency/procedures/scan', data),
  uploadCockpit:   (aircraftId, data)      => api.put(`/api/emergency/cockpit-image/${aircraftId}`, data),
  startSession:    (data)                  => api.post('/api/emergency/sessions/start', data),
  getNextItem:     (sessionId)             => api.get(`/api/emergency/sessions/${sessionId}/next-item`),
  itemDone:        (sessionId, data)       => api.post(`/api/emergency/sessions/${sessionId}/item-done`, data),
  completeSession: (sessionId, data = {}) => api.post(`/api/emergency/sessions/${sessionId}/complete`, data),
  sessionHistory:  (pilotId)               => api.get(`/api/emergency/sessions/history/${pilotId}`),
  pilotStats:      (pilotId)               => api.get(`/api/emergency/stats/${pilotId}`),
  overviewStats:   ()                      => api.get('/api/emergency/stats/overview'),
};

// ── Asistente IA de ayuda (Fase 1) — solo ayuda de uso ──
export const assistantService = {
  ask: (question, lang = 'es', history = []) =>
    api.post('/api/assistant/ask', { question, lang, history }),
};

// ── PR #6 (Vision) — Import de planillas históricas con Claude Vision ──
export const flightLogImportService = {
  // image: base64 sin prefijo data:; mediaType: image/jpeg | image/png
  scan:    (image, mediaType = 'image/jpeg') =>
             api.post('/api/vision/scan-flight-log', { image, mediaType }),
  import:  (payload) => api.post('/api/flight-log/import', payload),
  list:    ()         => api.get ('/api/flight-log/imports'),
  // Editar lote ya importado
  getRows: (id)            => api.get (`/api/flight-log/imports/${id}/rows`),
  update:  (id, payload)   => api.put (`/api/flight-log/imports/${id}`, payload),
  delete:  (id)       => api.delete(`/api/flight-log/imports/${id}`),
};

// ── Impersonation: el SuperAdmin puede ver el panel de cualquier org ──
// Cuando se setea window.__condor360_impersonate_org_id, todos los requests
// de GET lo incluyen como ?org_id= para que el tenantMiddleware filtre por esa org
api.interceptors.request.use((config) => {
  const orgId = window.__condor360_impersonate_org_id;
  if (orgId) {
    // GET → query param
    if (config.method === 'get') {
      config.params = { ...config.params, org_id: orgId };
    }
    // POST/PUT/PATCH → incluir en el body también
    else if (['post','put','patch'].includes(config.method)) {
      if (config.data && typeof config.data === 'string') {
        try {
          const body = JSON.parse(config.data);
          body._impersonate_org_id = orgId;
          config.data = JSON.stringify(body);
        } catch (_) {}
      } else if (config.data && typeof config.data === 'object') {
        config.data = { ...config.data, _impersonate_org_id: orgId };
      }
      // También como query param por si el controller lo lee de ahí
      config.params = { ...config.params, org_id: orgId };
    }
    // DELETE → query param
    else if (config.method === 'delete') {
      config.params = { ...config.params, org_id: orgId };
    }
  }
  return config;
});

export default api;
